import { Children, isValidElement, forwardRef, cloneElement } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// Recursively extract the readable text of a React node so we can match it
// against the search term (handles MenuItem -> Checkbox + ListItemText etc.).
export const getReactNodeText = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getReactNodeText).join(' ');
  if (isValidElement(node)) {
    const props = node.props || {};
    if (props.children != null) return getReactNodeText(props.children);
    if (props.primary != null) return getReactNodeText(props.primary);
    if (props.label != null) return getReactNodeText(props.label);
    if (typeof props.value === 'string' || typeof props.value === 'number') {
      return String(props.value);
    }
  }
  return '';
};

export const countMenuItems = (children) =>
  Children.toArray(children).filter(isValidElement).length;

const isKeptValue = (keep, value) =>
  keep.some((kept) => kept == value || String(kept) === String(value));

// Keep children whose text matches the search term. Selected values that do not
// match are still mounted (hidden) so MUI Select does not warn about out-of-range
// values, but they are no longer visible in the filtered list.
export const filterMenuChildren = (children, search, keepValues = []) => {
  if (!search) return children;
  const lower = search.toLowerCase();
  const keep = Array.isArray(keepValues) ? keepValues : [keepValues];
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) return [child];
    const matches = getReactNodeText(child).toLowerCase().includes(lower);
    if (matches) return [child];
    if (
      child.props?.value !== undefined &&
      isKeptValue(keep, child.props.value)
    ) {
      return [
        cloneElement(child, {
          'aria-hidden': true,
          tabIndex: -1,
          style: { ...(child.props.style || {}), display: 'none' },
          sx: {
            ...(typeof child.props.sx === 'object' && child.props.sx
              ? child.props.sx
              : {}),
            display: 'none',
          },
        }),
      ];
    }
    return [];
  });
};

// A sticky search input rendered at the top of a dropdown menu. It stops key
// and click propagation so the parent Select neither runs its type-ahead nor
// treats the box as a selectable option (Escape is still allowed to close).
// NOTE: props are intentionally named `searchValue`/`onSearchChange` (not
// `value`/`onChange`) so MUI <Select> does not treat this element as a
// selectable option when it scans children for a matching value.
export const MenuSearchField = forwardRef(
  ({ searchValue, onSearchChange, placeholder = 'Search...' }, ref) => (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'background.paper',
        px: 1,
        py: 0.75,
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') event.stopPropagation();
      }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <TextField
        inputRef={ref}
        size="small"
        fullWidth
        autoFocus
        value={searchValue}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  )
);

MenuSearchField.displayName = 'MenuSearchField';
